import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Layout as ReactGridLayout } from 'react-grid-layout';
import { colors } from '@dexma/ui-components';
import Layout, { LayoutType, LayoutElement, ConstraintsPerLayoutElement, LayoutElementType } from 'domain/entity/layout';
import { GridLayouts, GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { BREAKPOINTS, CELL_MARGIN, COLUMNS, Breakpoint } from 'adapter/in/dashboards/components/Grid';
import i18n from 'asset/translation';
import { elementFitsContraints } from 'adapter/in/dashboards/containers/Grid';
import { ALERT_ACTION_TYPE, dispatchAlertToaster, useAlertToaster } from 'adapter/in/common/context/Alert';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { isTablet, isDesktop, isSmartTV } from 'react-device-detect';
import { dispatchLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { computeLayoutRows } from 'adapter/in/dashboards/components/generic/ReactGrid/utils';
import {
    buildBreakpoint,
    calculateBreakpointDesktop,
    calculateCellSize,
    generateGridBackground,
    withDashboardFrame,
} from 'adapter/in/dashboards/containers/Grid/utils';

export const areGridLayoutsEqual = (layoutA: ReactGridLayout[], layoutB: ReactGridLayout[]): boolean => {
    const layoutBMap = new Map(layoutB.map((widgetLayout) => [widgetLayout.i, widgetLayout]));

    const isLayoutAaLayoutBSubset = layoutA.every((widgetLayoutA) => {
        const widgetLayoutB = layoutBMap.get(widgetLayoutA.i);

        if (widgetLayoutB) {
            if (
                widgetLayoutB.i === widgetLayoutA.i &&
                widgetLayoutB.w === widgetLayoutA.w &&
                widgetLayoutB.h === widgetLayoutA.h &&
                widgetLayoutB.x === widgetLayoutA.x &&
                widgetLayoutB.y === widgetLayoutA.y
            ) {
                return true;
            }
            return false;
        }
        return false;
    });

    return isLayoutAaLayoutBSubset && layoutA.length === layoutB.length;
};

export type WidgetBeingResized = GridLayoutElement & { fitsInGrid: boolean };
export const useGridView = (
    gridLayouts: GridLayouts,
    allGridLayouts: GridLayouts,
    editingLayout: boolean,
    selectedLayout: LayoutType,
    isEditEnabled: boolean,
    setAllGridLayouts: React.Dispatch<React.SetStateAction<GridLayouts>>,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>
): {
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    backgroundColor: string;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    editLayoutWidthStyle: string;
    layoutTotalRows: number;
    onWidthChange: (newWidth: number) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    widgetBeingResized?: WidgetBeingResized;
} => {
    const defaultLayoutType = (selectedLayoutParam: LayoutType): LayoutType => {
        if (selectedLayoutParam === LayoutType.A4) {
            return selectedLayoutParam;
        }
        if (isTablet) {
            return LayoutType.TABLET;
        }
        if (isDesktop || isSmartTV) {
            return LayoutType.DESKTOP;
        }
        return LayoutType.MOBILE;
    };
    const { currentUrl } = useNavigation();
    const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(defaultLayoutType(selectedLayout));
    const [breakpoints, setBreakpoints] = useState<{ [P: string]: number }>(buildBreakpoint(defaultLayoutType(selectedLayout)));
    const [widgetBeingResized, setWidgetBeingResized] = useState<undefined | WidgetBeingResized>(undefined);
    const [background, setBackground] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [layoutTotalRows, setLayoutTotalRows] = useState(0);
    const originalGridLayouts = gridLayouts;
    const [wrongSizeLayouts, setWrongSizeLayouts] = useState<LayoutType[]>([]);
    const { setHasLayoutChanges, setLayouts } = dispatchLayoutsView();
    const { setWarningAlert, setSuccessAlert } = dispatchAlertToaster();
    const { show: showAlert, type: alertType } = useAlertToaster();
    const cellSize = useMemo(() => calculateCellSize(containerWidth, currentBreakpoint), [containerWidth, currentBreakpoint]);

    const backgroundStyle = useMemo(
        () => generateGridBackground(background, cellSize, [CELL_MARGIN, CELL_MARGIN], COLUMNS[currentBreakpoint], containerWidth),
        [background, containerWidth, currentBreakpoint]
    );

    const style = useMemo(
        () => ({
            background: backgroundStyle,
        }),
        [backgroundStyle]
    );

    const addGridLayoutPropsToReactGrid = (layout: ReactGridLayout[]): GridLayoutElement[] => {
        if (!allGridLayouts[selectedLayout]) return [];
        const layoutElementsMap = allGridLayouts[selectedLayout].reduce((map, obj: GridLayoutElement) => {
            map.set(obj.i, obj);
            return map;
        }, new Map<string, GridLayoutElement>());
        return layout.map((reactGridLayoutElement: ReactGridLayout) => {
            const layoutElement = layoutElementsMap.get(reactGridLayoutElement.i);
            if (layoutElement) {
                return {
                    ...reactGridLayoutElement,
                    maxHeight: layoutElement.maxHeight,
                    type: layoutElement.type,
                    maxWidth: layoutElement.maxWidth,
                    minHeight: layoutElement.minHeight,
                    minWidth: layoutElement.minWidth,
                };
            }
            return {
                ...reactGridLayoutElement,
                maxHeight: 3,
                type: LayoutElementType.WIDGET,
                maxWidth: 3,
                minHeight: 3,
                minWidth: 3,
            };
        });
    };

    const handleLayoutChange = (_currentLayout: GridLayoutElement[], allLayouts: Record<string, ReactGridLayout[]>) => {
        if (!allLayouts[selectedLayout]?.length) return;

        const layoutWithProps = addGridLayoutPropsToReactGrid(allLayouts[selectedLayout]);

        const finalLayout = selectedLayout === LayoutType.A4 ? withDashboardFrame(layoutWithProps) : layoutWithProps;

        if (editingLayout) {
            setAllGridLayouts(() => ({
                ...allGridLayouts,
                [selectedLayout]: finalLayout,
            }));
        }

        setLayouts?.call(this, new Map([[selectedLayout, asLayout(finalLayout, selectedLayout)]]));

        const currentComparable = allLayouts[selectedLayout];
        const originalComparable = originalGridLayouts[selectedLayout] ?? [];

        setHasLayoutChanges?.call(this, originalComparable.length ? !areGridLayoutsEqual(currentComparable, originalComparable) : true);

        if (selectedLayout === LayoutType.A4) {
            setLayoutTotalRows(computeLayoutRows(finalLayout));
        }
    };

    useEffect(() => {
        if (selectedLayout === LayoutType.A4 && allGridLayouts[selectedLayout]?.length > 0) {
            const layoutWithProps = addGridLayoutPropsToReactGrid(allGridLayouts[selectedLayout]);

            const framed = withDashboardFrame(layoutWithProps);

            setLayouts?.call(this, new Map([[LayoutType.A4, asLayout(framed, LayoutType.A4)]]));

            setLayoutTotalRows(computeLayoutRows(framed));
        }
    }, []);

    const editLayoutWidthStyle =
        (editingLayout && breakpoints && Object.keys(breakpoints).length === 1 && Object.keys(breakpoints)[0] !== LayoutType.DESKTOP) ||
        (Object.keys(breakpoints).length === 1 && Object.keys(breakpoints)[0] === LayoutType.A4)
            ? `${breakpoints[Object.keys(breakpoints)[0]]}px`
            : '100%';

    useEffect(() => {
        // First time, when the container width is going from 0 to the actual size
        if (selectedLayout === LayoutType.A4) {
            setBreakpoints({ [LayoutType.A4]: BREAKPOINTS.A4 });
            return;
        }
        if (containerWidth > 0) {
            let currentInitialBreakpoint = LayoutType.MOBILE;
            if (isEditEnabled) {
                currentInitialBreakpoint = selectedLayout;
                setBreakpoints(buildBreakpoint(selectedLayout));
            } else if (isTablet) {
                currentInitialBreakpoint = LayoutType.TABLET;
            } else if (isDesktop || isSmartTV) {
                currentInitialBreakpoint = calculateBreakpointDesktop(containerWidth);
                setBreakpoints(BREAKPOINTS);
            }

            setCurrentBreakpoint(currentInitialBreakpoint);
        }
    }, [containerWidth, isEditEnabled, selectedLayout]);

    useEffect(() => {
        if (!widgetBeingResized) {
            Object.keys(wrongSizeWidgets).forEach((layoutType) => {
                if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length > 0 &&
                    !wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    setWrongSizeLayouts((prevState) => [...prevState, layoutType as LayoutType]);
                } else if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length === 0 &&
                    wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    const wrongSizeLayoutsCopy = [...wrongSizeLayouts];
                    const index = wrongSizeLayoutsCopy.findIndex((wrongLayoutCopy) => wrongLayoutCopy === layoutType);
                    wrongSizeLayoutsCopy.splice(index, 1);
                    setWrongSizeLayouts(wrongSizeLayoutsCopy);
                }
            });
        }
    }, [wrongSizeWidgets, widgetBeingResized?.i]);

    useEffect(() => {
        if (currentUrl.includes('/edit')) {
            if (
                showAlert &&
                wrongSizeLayouts.length === 0 &&
                Object.keys(allGridLayouts).length > 0 &&
                alertType === ALERT_ACTION_TYPE.WARNING
            ) {
                setSuccessAlert(i18n.t('dashboards.edit.dashBoardSizeIssuesSolved'));
            }
            if (wrongSizeLayouts.length > 0) {
                setWarningAlert(
                    i18n.t('dashboards.edit.dashboardHasSizeIssues', {
                        layoutTypes: wrongSizeLayouts
                            .map((layoutType: LayoutType) => i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`))
                            .toString()
                            .toLowerCase()
                            .replaceAll(',', ', '),
                    })
                );
            }
        }
    }, [currentUrl, wrongSizeLayouts, alertType, showAlert, allGridLayouts]);

    const onResize = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        setBackground(true);
        const layoutElementsMap = allGridLayouts[selectedLayout].reduce((map, obj: GridLayoutElement) => {
            map.set(obj.i, obj);
            return map;
        }, new Map<string, GridLayoutElement>());
        const foundElement = layoutElementsMap.get(newItem.i);
        if (foundElement) {
            const constraints = {
                minHeight: foundElement.minHeight,
                minWidth: foundElement.minWidth,
                maxHeight: foundElement.maxHeight,
                maxWidth: foundElement.maxWidth,
            };
            const elementBeingResizedFits = elementFitsContraints({
                ...newItem,
                ...constraints,
                type: foundElement.type,
            });
            if (elementBeingResizedFits) {
                setWidgetBeingResized({
                    ...newItem,
                    ...constraints,
                    type: foundElement.type,
                    fitsInGrid: true,
                });
            } else {
                setWidgetBeingResized({
                    ...newItem,
                    ...constraints,
                    type: foundElement.type,
                    fitsInGrid: false,
                });
            }
        }
    };

    const onResizeStop = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        // Synchro issues with layout if we dont mutate layout directly
        /*eslint-disable */
        setBackground(false);
        if (widgetBeingResized?.fitsInGrid === false) {
            const index = layout.findIndex((gridLayout) => gridLayout.i === newItem.i);
            layout[index] = oldItem;
        }
        setWidgetBeingResized(undefined);

        /* eslint-enable */
    };

    return {
        cellSize,
        backgroundStyle: background ? style : {},
        backgroundColor: editingLayout ? colors.gray200 : colors.gray100,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        layoutTotalRows,
        onDragStart: () => setBackground(true),
        onDragStop: () => setBackground(false),
        onResize,
        onResizeStop,
        onWidthChange: setContainerWidth,
        onBreakpointChange: setCurrentBreakpoint,
        onLayoutChange: handleLayoutChange,
        widgetBeingResized,
    };
};

const asLayout = (gridLayout: GridLayoutElement[], layoutType: LayoutType): Layout =>
    new Layout(
        layoutType,
        gridLayout.map((gridElement) => asWidgetLayout(gridElement))
    );

const asWidgetLayout = (gridElement: GridLayoutElement): LayoutElement => ({
    id: gridElement.i,
    position: {
        x: gridElement.x,
        y: gridElement.y,
    },
    size: {
        width: gridElement.w,
        height: gridElement.h,
    },
    type: gridElement.type,
    constraints: {
        maxHeight: gridElement.maxHeight,
        maxWidth: gridElement.maxWidth,
        minWidth: gridElement.minWidth,
        minHeight: gridElement.minHeight,
    },
});
